// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { toast } from 'sonner';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  ChevronRight,
  ChevronDown,
  GripVertical,
  Trophy,
  CalendarDays,
  CalendarRange,
  LayoutGrid,
  Plus,
  MoreHorizontal,
  Check,
  X,
  Users,
  ArrowUpRight,
  FileText,
  FilePlus,
  Pencil,
  Sparkles,
} from 'lucide-react';
import SettingsModal from '@/components/DataTable/SettingsModal';
import CreateCompetitionDialog from '@/components/CreateCompetitionDialog';
import PaymentModal from '@/components/DataTable/PaymentModal';
import AdminModal from '@/components/DataTable/AdminModal';
import TiebreakerModal from '@/components/DataTable/TiebreakerModal';
import CloneLeagueModal from '@/components/DataTable/CloneLeagueModal';
import useTournamentStore from '@/store/tournamentStore';
import useLeagueStore from '@/store/leagueStore';
import useDivisionStore from '@/store/divisionStore';
import useTeamDivisionStore from '@/store/teamDivisionStore';
import usePaymentProductStore from '@/store/paymentProductStore';
import useRegistrationStore from '@/store/registrationStore';
import useRegistrationTeamStore from '@/store/registrationTeamStore';
import useAdminStore from '@/store/adminStore';
import useTourStore from '@/store/tourStore';
import { startOrganizeTour, TOUR_ID as ORGANIZE_TOUR_ID } from '@/tours/organizeTour';
import MoveTeamEligibilityDialog from '@/components/MoveTeamEligibilityDialog';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { EmptyState } from '@/components/Page/EmptyState';
import { ListSkeleton } from '@/components/Page/PageSkeletons';
import helpers from '@/utils/helpers';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import getConfig from '@staty/shared/utils/getConfig';
import { boolOrInherit, chainOrInherit, inheritedBool } from '@/utils/boolOrInherit';
import { CONFIG_DEFAULTS } from '@staty/shared/utils/configDefaults';
import { getEffectivePaymentProduct } from '@staty/shared/utils';
import { deriveCapacityStates } from '@staty/shared/utils/waitlist';
import { isWithdrawn } from '@staty/shared/utils/registrationTeamStatus';

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatPlayerCountLabel(cfg) {
  const min = Number(cfg?.minPlayers);
  const max = Number(cfg?.maxPlayers);
  const hasMin = Number.isFinite(min) && min > 0;
  const hasMax = Number.isFinite(max) && max > 0;
  if (!hasMin && !hasMax) return null;
  if (hasMin && hasMax) {
    if (min === max) return `${min} player${min === 1 ? '' : 's'}`;
    return `${min}–${max} players`;
  }
  if (hasMin) return `min ${min} player${min === 1 ? '' : 's'}`;
  return `up to ${max} player${max === 1 ? '' : 's'}`;
}

// Format the eligibility constraints (gender / age / rating) on a config blob
// into short display strings, one per type that's actually set. Empty array
// means no eligibility constraints.
function formatEligibilityLabels(cfg) {
  const parts = [];
  const gender = Array.isArray(cfg?.gender) ? cfg.gender : [];
  if (gender.length > 0) {
    parts.push(gender.map((g) => g.charAt(0).toUpperCase() + g.slice(1)).join(' / '));
  }
  const minAge = Number(cfg?.age?.minAge);
  const maxAge = Number(cfg?.age?.maxAge);
  const hasMinAge = Number.isFinite(minAge) && minAge > 0;
  const hasMaxAge = Number.isFinite(maxAge) && maxAge > 0;
  if (hasMinAge && hasMaxAge) parts.push(`Age ${minAge}–${maxAge}`);
  else if (hasMinAge) parts.push(`Age ${minAge}+`);
  else if (hasMaxAge) parts.push(`Age ≤${maxAge}`);
  const minR = parseFloat(cfg?.rating?.minRating);
  const maxR = parseFloat(cfg?.rating?.maxRating);
  const hasMinR = Number.isFinite(minR) && minR > 0;
  const hasMaxR = Number.isFinite(maxR) && maxR > 0;
  if (hasMinR && hasMaxR) parts.push(`Rating ${minR}–${maxR}`);
  else if (hasMinR) parts.push(`Rating ${minR}+`);
  else if (hasMaxR) parts.push(`Rating ≤${maxR}`);
  return parts;
}

function resolvePaymentProduct(paymentProductRef, paymentProducts) {
  if (!paymentProductRef) return null;
  if (typeof paymentProductRef === 'object' && paymentProductRef.name) {
    return paymentProductRef; // already populated
  }
  return _.find(paymentProducts, { _id: paymentProductRef }) || null;
}

// ── Sortable Row Wrapper ────────────────────────────────────────────────────

function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ dragListeners: listeners })}
    </div>
  );
}

// ── Inline Add Row ──────────────────────────────────────────────────────────

function InlineAddRow({ placeholder, value, onChange, onSubmit, onCancel }) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div className="flex items-center gap-2 py-1 px-2">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        placeholder={placeholder}
        className="h-7 w-48 text-sm"
      />
      <Button size="sm" className="h-7 px-2" onClick={onSubmit}>
        <Check className="h-3 w-3" />
      </Button>
      <Button size="sm" variant="outline" className="h-7 px-2" onClick={onCancel}>
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

// ── Team Row ─────────────────────────────────────────────────────────────────

function TeamRow({ team, isWaitlisted, isPending, waitlistPosition, fromDivisionId }) {
  const displayName = getTeamDisplayName(team);
  // Draggable to move the team into another division of the same contest. The
  // grip handle carries the listeners so the "open team page" button still works.
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `team:${team._id}`,
    data: { fromDivisionId, registrationTeamId: helpers.getObjId(team.registrationTeam) },
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  // Full-ink like every other tree row (owner 2026-09-11) — only the grip
  // and badges stay muted; teams are content, not chrome.
  return (
    <div ref={setNodeRef} style={style} className="group flex items-center gap-1 py-0.5 px-2 rounded hover:bg-muted/50 text-[15px]">
      <span
        className="cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0"
        {...listeners}
        {...attributes}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </span>
      <Users className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="truncate">{displayName}</span>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 w-6 p-0 flex-shrink-0"
        onClick={() => window.open(`/admin/team/${team._id}`, '_blank')}
        title="Open team page"
      >
        <ArrowUpRight className="h-4 w-4" />
      </Button>
      {isWaitlisted && (
        <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset flex-shrink-0 bg-warning/10 text-warning ring-warning/30">
          Waitlist{waitlistPosition ? ` #${waitlistPosition}` : ''}
        </span>
      )}
      {isPending && (
        <span
          className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset flex-shrink-0 bg-muted text-muted-foreground ring-border"
          title="No payment yet: holds no spot until partially paid"
        >
          Pending
        </span>
      )}
    </div>
  );
}

// ── Division Row ────────────────────────────────────────────────────────────

// Exported for tests only — the page renders it internally.
export function DivisionRow({
  division,
  isExpanded,
  onToggle,
  teams,
  teamsLoaded,
  paymentProducts,
  parentEffectiveProduct,
  parentEffectiveCapacityConfig,
  parentEffectivePlayerConfig,
  parentEffectiveEligibilityConfig,
  editingId, editingName, editingType,
  onStartEdit, onEditNameChange, onSaveEdit, onCancelEdit,
  onOptionsClick,
  // Whether the ORGANIZATION has a sport — the only correct source (CLAUDE.md).
  // Gates the Tiebreakers menu item, matching LeagueRow / TournamentRow.
  orgHasSport,
  dragListeners,
  isDropTarget,
  isDropZoneActive,
}) {
  const isEditing = editingId === division._id && editingType === 'division';
  const ownProduct = resolvePaymentProduct(division.config?.paymentProduct, paymentProducts);
  const product = getEffectivePaymentProduct(ownProduct, parentEffectiveProduct);
  const ownCapacity = division.config?.capacity || 0;
  const hasOwnCapacity = ownCapacity > 0;
  // hasWaitlist is meaningless without capacity, so it follows capacity inheritance:
  // when the division has no own capacity, inherit both capacity and waitlist from parent.
  // (Mongoose defaults hasWaitlist=false, so we can't use undefined-check to detect "own".)
  const effectiveCapacity = hasOwnCapacity ? ownCapacity : (parentEffectiveCapacityConfig?.capacity || 0);
  const effectiveHasWaitlist = hasOwnCapacity
    ? !!division.config?.hasWaitlist
    : !!(parentEffectiveCapacityConfig?.hasWaitlist);
  const capacityInherited = !hasOwnCapacity && effectiveCapacity > 0;
  const waitlistInherited = !hasOwnCapacity && effectiveHasWaitlist;
  const effectivePlayerConfig = {
    minPlayers: division.config?.minPlayers ?? parentEffectivePlayerConfig?.minPlayers,
    maxPlayers: division.config?.maxPlayers ?? parentEffectivePlayerConfig?.maxPlayers,
  };
  const playerLabel = formatPlayerCountLabel(effectivePlayerConfig);
  // Eligibility blends own-first / parent-fallback per type so each row reflects
  // what actually applies to its registrants.
  const ownElig = division.config?.eligibility || {};
  const ownGender = Array.isArray(ownElig.gender) ? ownElig.gender : [];
  const ownAge = ownElig.age;
  const ownRating = ownElig.rating;
  const effectiveEligibilityConfig = {
    gender: ownGender.length > 0 ? ownGender : parentEffectiveEligibilityConfig?.gender,
    age: ownAge && (ownAge.minAge || ownAge.maxAge) ? ownAge : parentEffectiveEligibilityConfig?.age,
    rating: ownRating && (ownRating.minRating || ownRating.maxRating) ? ownRating : parentEffectiveEligibilityConfig?.rating,
  };
  const eligibilityParts = formatEligibilityLabels(effectiveEligibilityConfig);
  const priceLabel = product
    ? product.isFree
      ? 'Free'
      : `${helpers.convertToUSD(product.amount)}${product.perTeam ? '/team' : product.perPlayer ? '/player' : ''}`
    : null;

  // Per-team capacity state: only meaningful when the division actually has a
  // cap. deriveCapacityStates needs RegistrationTeam-shaped objects (hasPaid,
  // created, promotedAt, plus the new paymentReceived from the backend) —
  // divisionTeam.findByLeagueId populates registrationTeam + paymentReceived.
  // A team only holds a spot with a partial payment, or if the division is free.
  const divIsFree = product ? (!!product.isFree || !Number(product.amount)) : true;
  // A team is "free" (holds a spot without paying) ONLY when the DIVISION/product
  // is free. A team whose resolved cost is merely $0 because it has an EMPTY
  // roster (a per-player team with no players) is an incomplete registration, not
  // a free team — it must NOT be treated as free, so with no payment it stays
  // 'pending' and is hidden from this competition view.
  const capacityInput = teams
    .map((t) => {
      const rt = t.registrationTeam;
      if (!rt) return null;
      return { ...rt, isFree: divIsFree };
    })
    .filter(Boolean);
  // Manually-added teams (active, no RT link — admin Add Team) consume seats
  // (ADR 0056): they seat ahead of registrants, so linked teams derive against
  // the remaining capacity. Same rule as the server's manualSeats count.
  const manualSeatCount = teams.filter((t) => t.active === true && !t.registrationTeam).length;
  const capacityStates = effectiveCapacity > 0
    ? deriveCapacityStates(
        capacityInput,
        {
          manualSeats: manualSeatCount,
          config: {
            capacity: effectiveCapacity,
            hasWaitlist: effectiveHasWaitlist,
            promotionPaymentWindowHours:
              division.config?.promotionPaymentWindowHours
              ?? parentEffectiveCapacityConfig?.promotionPaymentWindowHours,
          },
        },
      )
    : null;
  // Waitlist position (#N): FIFO by effective entry time (promotedAt ?? created),
  // matching registrationCapacity.getWaitlistPosition so the number agrees with
  // the registration page.
  const waitlistPositionByRtId = new Map();
  if (capacityStates) {
    capacityInput
      .filter((rt) => capacityStates.get(String(rt._id)) === 'waitlisted')
      .sort((a, b) =>
        new Date(a.promotedAt || a.created).getTime() - new Date(b.promotedAt || b.created).getTime())
      .forEach((rt, i) => waitlistPositionByRtId.set(String(rt._id), i + 1));
  }
  // Display the OCCUPYING count (secured + waitlisted) over capacity — so an
  // over-capacity division reads e.g. 7 / 6 (6 secured + 1 waitlisted). Unpaid
  // 'pending' teams hold no spot and are excluded; manually-added teams hold
  // seats and are included (ADR 0056). Falls back to raw count only for
  // unlimited divisions (capacityStates null → no denominator shown).
  const occupyingCount = capacityStates
    ? [...capacityStates.values()].filter((s) => s === 'secured' || s === 'waitlisted').length
      + manualSeatCount
    : teams.length;
  // Only show teams that hold a spot (secured/waitlisted = have at least a
  // partial payment, or are free). 'pending' (unpaid) teams are hidden. For
  // unlimited divisions (capacityStates null) there's no gating, so all show.
  const visibleTeamRows = teams
    .map((team) => {
      const rtId = team.registrationTeam?._id || team.registrationTeam;
      const state = capacityStates && rtId ? capacityStates.get(String(rtId)) : null;
      return { team, state };
    })
    .filter(({ state }) => state !== 'pending');
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div
        className={`group flex items-center gap-1 py-1 px-2 rounded text-[15px] transition-colors ${
          isDropTarget
            ? 'bg-primary/10 ring-2 ring-ring'
            : isDropZoneActive
              ? 'ring-1 ring-ring/30'
              : 'hover:bg-muted'
        }`}
      >
        <span
          className="cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0"
          data-tour="organize-row-drag"
          title="Drag to reorder"
          {...dragListeners}
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="icon" data-tour="organize-row-expand" aria-label={isExpanded ? "Collapse" : "Expand"} title={isExpanded ? "Collapse" : "Expand to see what's inside"} className="h-5 w-5 p-0 flex-shrink-0">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <LayoutGrid className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        {isEditing ? (
          <div className="flex items-center gap-1 flex-1">
            <Input
              value={editingName}
              onChange={(e) => onEditNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveEdit();
                if (e.key === 'Escape') onCancelEdit();
              }}
              className="h-6 text-sm flex-1 max-w-48"
              autoFocus
            />
            <Button size="sm" variant="ghost" aria-label="Save name" className="h-6 w-6 p-0" onClick={onSaveEdit}>
              <Check className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" aria-label="Cancel rename" className="h-6 w-6 p-0" onClick={onCancelEdit}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex-1 flex items-center gap-1 min-w-0">
            <span
              data-tour="organize-row-name"
              title="Double-click to rename"
              className="min-w-0 max-w-full cursor-pointer select-none truncate"
              onDoubleClick={() => onStartEdit(division._id, division.name, 'division')}
            >
              {division.name}
              {teamsLoaded && (
                <span className={`font-normal ml-1 text-xs ${capacityInherited ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
                  ({occupyingCount}{effectiveCapacity > 0 ? ` / ${effectiveCapacity}` : ''})
                </span>
              )}
            </span>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Rename"
              title="Rename"
              className="h-5 w-5 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
              onClick={() => onStartEdit(division._id, division.name, 'division')}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 flex-shrink-0"
              onClick={() => window.open(`/admin/division/${division._id}`, '_blank')}
              title="Open division page"
            >
              <ArrowUpRight className="h-4 w-4" />
            </Button>
            {effectiveHasWaitlist && (
              <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset flex-shrink-0 ${waitlistInherited ? 'bg-warning/5 text-warning/60 ring-warning/20' : 'bg-warning/10 text-warning ring-warning/30'}`}>
                Waitlist
              </span>
            )}
          </div>
        )}
        {priceLabel && (
          <span className="text-xs text-muted-foreground flex-shrink-0">{priceLabel}</span>
        )}
        {playerLabel && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
            <Users className="h-3 w-3" />
            {playerLabel}
          </span>
        )}
        {eligibilityParts.map((label) => (
          <span key={label} className="text-xs text-muted-foreground flex-shrink-0">
            {label}
          </span>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" data-tour="organize-row-menu" aria-label="More options" title="Settings, payment, clone & more" className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onOptionsClick('settings', division._id, 'division')}>Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOptionsClick('payment', division._id, 'division')}>Payment</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOptionsClick('admin', division._id, 'division')}>Admins</DropdownMenuItem>
            {/* Gate on the ORG's sport, like the contest and tournament rows do.
                This used to read `division.sports` — the denormalized mirror
                CLAUDE.md says never to read from — which `division.update`
                strips on every write, so it is only ever set at create time.
                Divisions predating that helper had an empty array with no way to
                backfill it, so this item was permanently hidden for them while
                their parent rows showed it. The modal is already org-scoped
                (see `sports={[organization.sports[0]]}` at the render site), so
                the old gate guarded a condition the modal never consulted. */}
            {orgHasSport && (
              <DropdownMenuItem onClick={() => onOptionsClick('tiebreaker', division._id, 'division')}>Tiebreakers</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onOptionsClick('clone', division._id, 'division')}>Clone</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onOptionsClick('delete', division._id, 'division', division.name)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CollapsibleContent>
        <div className="ml-8 border-l border-border pl-2">
          {visibleTeamRows.map(({ team, state }) => (
            <TeamRow
              key={team._id}
              team={team}
              isWaitlisted={state === 'waitlisted'}
              waitlistPosition={waitlistPositionByRtId.get(String(team.registrationTeam?._id || team.registrationTeam))}
              fromDivisionId={division._id}
            />
          ))}
          {visibleTeamRows.length === 0 && (
            <div className="py-0.5 px-2 text-xs text-muted-foreground italic">No teams</div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── League Row ──────────────────────────────────────────────────────────────

function LeagueRow({
  league,
  registration,
  onNavigate,
  isExpanded,
  onToggle,
  divisions,
  teamDivisions,
  expandedDivisions,
  onToggleDivision,
  loadedLeagueTeams,
  paymentProducts,
  parentTournamentProduct,
  parentTournamentConfig,
  parentTournamentId,
  orgHasSport,
  editingId, editingName, editingType,
  onStartEdit, onEditNameChange, onSaveEdit, onCancelEdit,
  onRequestActiveToggle,
  onOptionsClick,
  onAddChild,
  addingChild, newItemName, onNewItemNameChange, onAddItem, onCancelAdd,
  dragListeners,
}) {
  const isEditing = editingId === league._id && editingType === 'league';
  const leagueDivisions = divisions;
  const ownLeagueProduct = resolvePaymentProduct(league.config?.paymentProduct, paymentProducts);
  const leagueProduct = getEffectivePaymentProduct(ownLeagueProduct, parentTournamentProduct);
  const ownLeagueCapacity = league.config?.capacity || 0;
  const hasOwnLeagueCapacity = ownLeagueCapacity > 0;
  // See DivisionRow: hasWaitlist follows capacity inheritance, since defaults
  // make a pure undefined check unreliable.
  const effectiveLeagueCapacity = hasOwnLeagueCapacity ? ownLeagueCapacity : (parentTournamentConfig?.capacity || 0);
  const effectiveLeagueHasWaitlist = hasOwnLeagueCapacity
    ? !!league.config?.hasWaitlist
    : !!(parentTournamentConfig?.hasWaitlist);
  const leagueWaitlistInherited = !hasOwnLeagueCapacity && effectiveLeagueHasWaitlist;
  const effectiveCapacityConfig = { capacity: effectiveLeagueCapacity, hasWaitlist: effectiveLeagueHasWaitlist };
  const leaguePriceLabel = leagueProduct
    ? leagueProduct.isFree
      ? 'Free'
      : `${helpers.convertToUSD(leagueProduct.amount)}${leagueProduct.perTeam ? '/team' : leagueProduct.perPlayer ? '/player' : ''}`
    : null;
  const effectiveLeaguePlayerConfig = {
    minPlayers: league.config?.minPlayers ?? parentTournamentConfig?.minPlayers,
    maxPlayers: league.config?.maxPlayers ?? parentTournamentConfig?.maxPlayers,
  };
  const leaguePlayerLabel = formatPlayerCountLabel(effectiveLeaguePlayerConfig);
  const ownLeagueElig = league.config?.eligibility || {};
  const parentTournamentElig = parentTournamentConfig?.eligibility || {};
  const ownLeagueGender = Array.isArray(ownLeagueElig.gender) ? ownLeagueElig.gender : [];
  const ownLeagueAge = ownLeagueElig.age;
  const ownLeagueRating = ownLeagueElig.rating;
  const effectiveLeagueEligibilityConfig = {
    gender: ownLeagueGender.length > 0 ? ownLeagueGender : parentTournamentElig.gender,
    age: ownLeagueAge && (ownLeagueAge.minAge || ownLeagueAge.maxAge) ? ownLeagueAge : parentTournamentElig.age,
    rating: ownLeagueRating && (ownLeagueRating.minRating || ownLeagueRating.maxRating) ? ownLeagueRating : parentTournamentElig.rating,
  };
  const leagueEligibilityParts = formatEligibilityLabels(effectiveLeagueEligibilityConfig);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Live drag affordance: while a TEAM is being dragged, highlight the division
  // under the cursor as the drop target. `teamDrag` is non-null only during a
  // team drag (not division-reorder); `overDivisionId` is the hovered division.
  const [teamDrag, setTeamDrag] = useState<{ fromDivisionId: string } | null>(null);
  const [overDivisionId, setOverDivisionId] = useState<string | null>(null);
  const clearDrag = () => { setTeamDrag(null); setOverDivisionId(null); };

  const handleDivisionDragStart = ({ active }) => {
    if (String(active.id).startsWith('team:')) {
      setTeamDrag({ fromDivisionId: active.data?.current?.fromDivisionId });
    }
  };

  const handleDragOver = ({ over }) => {
    setOverDivisionId(over ? String(over.id) : null);
  };

  const handleDivisionDragEnd = ({ active, over }) => {
    if (!over) return;
    const activeId = String(active.id);

    // Team move: a team was dragged onto a division of this same contest. Team
    // ids are namespaced `team:<divisionTeamId>`; division nodes are bare ids.
    if (activeId.startsWith('team:')) {
      const divisionTeamId = activeId.slice('team:'.length);
      const targetDivisionId = String(over.id);
      const dt = _.find(teamDivisions, (t) => t._id === divisionTeamId);
      if (!dt) return;
      const fromDivisionId = helpers.getObjId(dt.division);
      // Only a real cross-division drop within this league is a move.
      if (targetDivisionId === fromDivisionId) return;
      if (!leagueDivisions.some((d) => d._id === targetDivisionId)) return;
      onOptionsClick('move-team', divisionTeamId, 'division', null, { divisionTeam: dt, targetDivisionId });
      return;
    }

    // Division reorder (existing behavior).
    if (activeId === String(over.id)) return;
    const oldIdx = leagueDivisions.findIndex((d) => d._id === active.id);
    const newIdx = leagueDivisions.findIndex((d) => d._id === over.id);
    const reordered = arrayMove([...leagueDivisions], oldIdx, newIdx);
    reordered.forEach((d, i) => onOptionsClick('reorder-division', d._id, 'division', null, { ...d, priority: reordered.length - i }));
  };

  const showAddRow = addingChild?.type === 'division' && addingChild?.parentId === league._id;

  const scheduleUrl = parentTournamentId
    ? `/admin/organization/tournament/${parentTournamentId}/contest/${league._id}/divisions/schedule`
    : `/admin/organization/league/${league._id}/divisions/schedule`;

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className="group flex items-center gap-1 py-1 px-2 rounded hover:bg-muted transition-colors text-[15px]">
        <span
          className="cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0"
          data-tour="organize-row-drag"
          title="Drag to reorder"
          {...dragListeners}
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="icon" data-tour="organize-row-expand" aria-label={isExpanded ? "Collapse" : "Expand"} title={isExpanded ? "Collapse" : "Expand to see what's inside"} className="h-5 w-5 p-0 flex-shrink-0">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        {isEditing ? (
          <div className="flex items-center gap-1 flex-1">
            <Input
              value={editingName}
              onChange={(e) => onEditNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveEdit();
                if (e.key === 'Escape') onCancelEdit();
              }}
              className="h-6 text-sm flex-1 max-w-48"
              autoFocus
            />
            <Button size="sm" variant="ghost" aria-label="Save name" className="h-6 w-6 p-0" onClick={onSaveEdit}>
              <Check className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" aria-label="Cancel rename" className="h-6 w-6 p-0" onClick={onCancelEdit}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex-1 flex items-center gap-1 min-w-0">
            <span
              data-tour="organize-row-name"
              title="Double-click to rename"
              className={`min-w-0 max-w-full cursor-pointer select-none truncate${!league.active ? ' text-muted-foreground' : ''}`}
              onDoubleClick={() => onStartEdit(league._id, league.name, 'league')}
            >
              {league.name}
            </span>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Rename"
              title="Rename"
              className="h-5 w-5 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
              onClick={() => onStartEdit(league._id, league.name, 'league')}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            {effectiveLeagueHasWaitlist && (
              <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset flex-shrink-0 ${leagueWaitlistInherited ? 'bg-warning/5 text-warning/60 ring-warning/20' : 'bg-warning/10 text-warning ring-warning/30'}`}>
                Waitlist
              </span>
            )}
          </div>
        )}
        {leaguePriceLabel && (
          <span className="text-xs text-muted-foreground flex-shrink-0">{leaguePriceLabel}</span>
        )}
        {leaguePlayerLabel && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
            <Users className="h-3 w-3" />
            {leaguePlayerLabel}
          </span>
        )}
        {leagueEligibilityParts.map((label) => (
          <span key={label} className="text-xs text-muted-foreground flex-shrink-0">
            {label}
          </span>
        ))}
        <Switch
          checked={!!league.active}
          onCheckedChange={(checked) => onRequestActiveToggle(league._id, league.name, 'league', checked)}
          data-tour="organize-row-active"
          title="Active: shown on your public site. Switch off to archive."
          className="flex-shrink-0 scale-75"
        />
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 flex-shrink-0"
          onClick={() => window.open(scheduleUrl, '_blank')}
          title="Division schedule"
        >
          <CalendarRange className="h-3.5 w-3.5" />
        </Button>
        {onNavigate && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
            onClick={() =>
              onNavigate(
                registration
                  ? `/admin/registration/${registration._id}/edit`
                  : `/admin/registration?create=league&parentId=${league._id}`
              )
            }
            title={registration ? 'Edit registration' : 'Create registration'}
          >
            {registration ? <FileText className="h-3 w-3" /> : <FilePlus className="h-3 w-3" />}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
          onClick={() => onAddChild(league._id, 'division')}
          title="Add Division"
        >
          <Plus className="h-3 w-3" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" data-tour="organize-row-menu" aria-label="More options" title="Settings, payment, clone & more" className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onOptionsClick('settings', league._id, 'league')}>Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOptionsClick('payment', league._id, 'league')}>Payment</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOptionsClick('admin', league._id, 'league')}>Admins</DropdownMenuItem>
            {orgHasSport && (
              <DropdownMenuItem onClick={() => onOptionsClick('tiebreaker', league._id, 'league')}>Tiebreakers</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onOptionsClick('clone', league._id, 'league')}>Clone</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onOptionsClick('delete', league._id, 'league', league.name)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CollapsibleContent>
        <div className="ml-8 border-l border-border pl-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDivisionDragStart}
            onDragOver={handleDragOver}
            onDragEnd={(e) => { handleDivisionDragEnd(e); clearDrag(); }}
            onDragCancel={clearDrag}
          >
            <SortableContext items={leagueDivisions.map((d) => d._id)} strategy={verticalListSortingStrategy}>
              {leagueDivisions.map((division) => (
                <SortableRow key={division._id} id={division._id}>
                  {({ dragListeners: divDragListeners }) => (
                    <DivisionRow
                      division={division}
                      isExpanded={expandedDivisions.has(division._id)}
                      onToggle={() => onToggleDivision(division._id)}
                      teams={_.orderBy(
                        // Withdrawn teams are hidden from the structure tree —
                        // they're no longer part of the competition. The populated
                        // registrationTeam is already on every row (the store
                        // requests include=...,registrationTeam), so no extra fetch.
                        _.filter(teamDivisions, (t) =>
                          helpers.getObjId(t.division) === division._id
                          && !isWithdrawn(t.registrationTeam)),
                        ['seed', 'name'], ['asc', 'asc']
                      )}
                      teamsLoaded={loadedLeagueTeams.has(league._id)}
                      paymentProducts={paymentProducts}
                      parentEffectiveProduct={leagueProduct}
                      parentEffectiveCapacityConfig={effectiveCapacityConfig}
                      parentEffectivePlayerConfig={effectiveLeaguePlayerConfig}
                      parentEffectiveEligibilityConfig={effectiveLeagueEligibilityConfig}
                      editingId={editingId}
                      editingName={editingName}
                      editingType={editingType}
                      onStartEdit={onStartEdit}
                      onEditNameChange={onEditNameChange}
                      onSaveEdit={onSaveEdit}
                      onCancelEdit={onCancelEdit}
                      onOptionsClick={onOptionsClick}
                      orgHasSport={orgHasSport}
                      dragListeners={divDragListeners}
                      isDropTarget={!!teamDrag && overDivisionId === division._id && division._id !== teamDrag.fromDivisionId}
                      isDropZoneActive={!!teamDrag && division._id !== teamDrag.fromDivisionId}
                    />
                  )}
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
          {showAddRow && (
            <InlineAddRow
              placeholder="Division name..."
              value={newItemName}
              onChange={onNewItemNameChange}
              onSubmit={onAddItem}
              onCancel={onCancelAdd}
            />
          )}
          {leagueDivisions.length === 0 && !showAddRow && (
            <div className="py-1 px-2 text-xs text-muted-foreground italic">No divisions</div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Tournament Row ──────────────────────────────────────────────────────────

function TournamentRow({
  tournament,
  registration,
  onNavigate,
  isExpanded,
  onToggle,
  contests,
  orgHasSport,
  expandedLeagues,
  onToggleLeague,
  divisions,
  teamDivisions,
  expandedDivisions,
  onToggleDivision,
  loadedLeagueTeams,
  editingId, editingName, editingType,
  onStartEdit, onEditNameChange, onSaveEdit, onCancelEdit,
  onRequestActiveToggle,
  onOptionsClick,
  onAddChild,
  paymentProducts,
  addingChild, newItemName, onNewItemNameChange, onAddItem, onCancelAdd,
  dragListeners,
}) {
  const isEditing = editingId === tournament._id && editingType === 'tournament';
  const tournamentProduct = resolvePaymentProduct(tournament.config?.paymentProduct, paymentProducts);
  const tournamentPriceLabel = tournamentProduct
    ? tournamentProduct.isFree
      ? 'Free'
      : `${helpers.convertToUSD(tournamentProduct.amount)}${tournamentProduct.perTeam ? '/team' : tournamentProduct.perPlayer ? '/player' : ''}`
    : null;
  const tournamentPlayerLabel = formatPlayerCountLabel(tournament.config);
  const tournamentEligibilityParts = formatEligibilityLabels(tournament.config?.eligibility);
  const tournamentHasWaitlist = !!tournament.config?.hasWaitlist;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleContestDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = contests.findIndex((l) => l._id === active.id);
    const newIdx = contests.findIndex((l) => l._id === over.id);
    const reordered = arrayMove([...contests], oldIdx, newIdx);
    reordered.forEach((l, i) => onOptionsClick('reorder-league', l._id, 'league', null, { ...l, priority: reordered.length - i }));
  };

  const showAddRow = addingChild?.type === 'league' && addingChild?.parentId === tournament._id;

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className="group flex items-center gap-1 py-1.5 px-2 rounded hover:bg-muted transition-colors text-base font-medium">
        <span
          className="cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0"
          data-tour="organize-row-drag"
          title="Drag to reorder"
          {...dragListeners}
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="icon" data-tour="organize-row-expand" aria-label={isExpanded ? "Collapse" : "Expand"} title={isExpanded ? "Collapse" : "Expand to see what's inside"} className="h-5 w-5 p-0 flex-shrink-0">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <Trophy className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        {isEditing ? (
          <div className="flex items-center gap-1 flex-1">
            <Input
              value={editingName}
              onChange={(e) => onEditNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveEdit();
                if (e.key === 'Escape') onCancelEdit();
              }}
              className="h-6 text-sm flex-1 max-w-48 font-normal"
              autoFocus
            />
            <Button size="sm" variant="ghost" aria-label="Save name" className="h-6 w-6 p-0" onClick={onSaveEdit}>
              <Check className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" aria-label="Cancel rename" className="h-6 w-6 p-0" onClick={onCancelEdit}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex-1 flex items-center gap-1 min-w-0">
            <span
              data-tour="organize-row-name"
              title="Double-click to rename"
              className={`min-w-0 max-w-full cursor-pointer select-none truncate${!tournament.active ? ' text-muted-foreground' : ''}`}
              onDoubleClick={() => onStartEdit(tournament._id, tournament.name, 'tournament')}
            >
              {tournament.name}
            </span>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Rename"
              title="Rename"
              className="h-5 w-5 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
              onClick={() => onStartEdit(tournament._id, tournament.name, 'tournament')}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            {tournamentHasWaitlist && (
              <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset flex-shrink-0 bg-warning/10 text-warning ring-warning/30">
                Waitlist
              </span>
            )}
          </div>
        )}
        {tournamentPriceLabel && (
          <span className="text-xs text-muted-foreground flex-shrink-0">{tournamentPriceLabel}</span>
        )}
        {tournamentPlayerLabel && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
            <Users className="h-3 w-3" />
            {tournamentPlayerLabel}
          </span>
        )}
        {tournamentEligibilityParts.map((label) => (
          <span key={label} className="text-xs text-muted-foreground flex-shrink-0">
            {label}
          </span>
        ))}
        <Switch
          checked={!!tournament.active}
          onCheckedChange={(checked) => onRequestActiveToggle(tournament._id, tournament.name, 'tournament', checked)}
          data-tour="organize-row-active"
          title="Active: shown on your public site. Switch off to archive."
          className="flex-shrink-0 scale-75"
        />
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
          onClick={() =>
            onNavigate(
              registration
                ? `/admin/registration/${registration._id}/edit`
                : `/admin/registration?create=tournament&parentId=${tournament._id}`
            )
          }
          title={registration ? 'Edit registration' : 'Create registration'}
        >
          {registration ? <FileText className="h-3 w-3" /> : <FilePlus className="h-3 w-3" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
          onClick={() => onNavigate(`/admin/scheduler-day2/tournament/${tournament._id}`)}
          title="Open schedule"
        >
          <CalendarDays className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
          onClick={() => onAddChild(tournament._id, 'league')}
          title="Add League"
        >
          <Plus className="h-3 w-3" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" data-tour="organize-row-menu" aria-label="More options" title="Settings, payment, clone & more" className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onOptionsClick('settings', tournament._id, 'tournament')}>Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOptionsClick('payment', tournament._id, 'tournament')}>Payment</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOptionsClick('admin', tournament._id, 'tournament')}>Admins</DropdownMenuItem>
            {orgHasSport && (
              <DropdownMenuItem onClick={() => onOptionsClick('tiebreaker', tournament._id, 'tournament')}>Tiebreakers</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onOptionsClick('clone', tournament._id, 'tournament')}>Clone</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onOptionsClick('delete', tournament._id, 'tournament', tournament.name)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CollapsibleContent>
        <div className="ml-8 border-l border-border pl-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleContestDragEnd}
          >
            <SortableContext items={contests.map((l) => l._id)} strategy={verticalListSortingStrategy}>
              {contests.map((league) => (
                <SortableRow key={league._id} id={league._id}>
                  {({ dragListeners: leagueDragListeners }) => (
                    <LeagueRow
                      league={league}
                      isExpanded={expandedLeagues.has(league._id)}
                      onToggle={() => onToggleLeague(league._id)}
                      divisions={_.orderBy(
                        _.filter(divisions, (d) => helpers.getObjId(d.league) === league._id),
                        ['priority', 'name'], ['desc', 'asc']
                      )}
                      paymentProducts={paymentProducts}
                      parentTournamentProduct={tournamentProduct}
                      parentTournamentConfig={tournament.config}
                      parentTournamentId={tournament._id}
                      orgHasSport={orgHasSport}
                      editingId={editingId}
                      editingName={editingName}
                      editingType={editingType}
                      onStartEdit={onStartEdit}
                      onEditNameChange={onEditNameChange}
                      onSaveEdit={onSaveEdit}
                      onCancelEdit={onCancelEdit}
                      onRequestActiveToggle={onRequestActiveToggle}
                      onOptionsClick={onOptionsClick}
                      onAddChild={onAddChild}
                      addingChild={addingChild}
                      newItemName={newItemName}
                      onNewItemNameChange={onNewItemNameChange}
                      onAddItem={onAddItem}
                      onCancelAdd={onCancelAdd}
                      teamDivisions={teamDivisions}
                      expandedDivisions={expandedDivisions}
                      onToggleDivision={onToggleDivision}
                      loadedLeagueTeams={loadedLeagueTeams}
                      dragListeners={leagueDragListeners}
                    />
                  )}
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
          {showAddRow && (
            <InlineAddRow
              placeholder="League name..."
              value={newItemName}
              onChange={onNewItemNameChange}
              onSubmit={onAddItem}
              onCancel={onCancelAdd}
            />
          )}
          {contests.length === 0 && !showAddRow && (
            <div className="py-1 px-2 text-xs text-muted-foreground italic">No leagues</div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

function OrganizeAdmin() {
  const { leagueName, league: organization } = useOutletContext() as any;
  const { getToken } = useAuth();

  // ── Store bindings ──
  const {
    tournaments,
    updateTournament,
    deleteTournament,
    patchTournament,
    cloneTournament,
    getTournamentsByLeagueName,
  } = useTournamentStore((state: any) => ({
    tournaments: state.tournaments,
    updateTournament: state.updateTournament(getToken),
    deleteTournament: state.deleteTournament(getToken),
    patchTournament: state.patchTournament(getToken),
    cloneTournament: state.cloneTournament(getToken),
    getTournamentsByLeagueName: state.getTournamentsByLeagueName,
  }));

  const {
    leagues,
    addLeague,
    updateLeague,
    deleteLeague,
    cloneLeague,
    getLeaguesByOrganizationName,
    getContestsByTournamentId,
  } = useLeagueStore((state: any) => ({
    leagues: state.leagues,
    addLeague: state.addLeague(getToken),
    updateLeague: state.updateLeague(getToken),
    deleteLeague: state.deleteLeague(getToken),
    cloneLeague: state.cloneLeague(getToken),
    getLeaguesByOrganizationName: state.getLeaguesByOrganizationName,
    getContestsByTournamentId: state.getContestsByTournamentId,
  }));

  // NOTE: updateDivision and deleteDivision take raw token, NOT curried
  const { divisions, addDivision, updateDivision, deleteDivision, getDivisionUsage, getDivisionsByLeagueId, cloneDivision } =
    useDivisionStore((state: any) => ({
      divisions: state.divisions,
      addDivision: state.addDivision(getToken),
      updateDivision: state.updateDivision,
      deleteDivision: state.deleteDivision,
      getDivisionUsage: state.getDivisionUsage,
      getDivisionsByLeagueId: state.getDivisionsByLeagueId,
      cloneDivision: state.cloneDivision(getToken),
    }));

  const { paymentProducts, getPaymentProductsByLeagueName } = usePaymentProductStore((state: any) => ({
    paymentProducts: state.paymentProducts,
    getPaymentProductsByLeagueName: state.getPaymentProductsByLeagueName(getToken),
  }));

  const { teamDivisions, getTeamDivisionsBySeasonId, updateTeamDivision } = useTeamDivisionStore((state: any) => ({
    teamDivisions: state.teamDivisions,
    getTeamDivisionsBySeasonId: state.getTeamDivisionsBySeasonId,
    updateTeamDivision: state.updateTeamDivision(getToken),
  }));

  const { checkDivisionEligibility } = useRegistrationTeamStore((state: any) => ({
    checkDivisionEligibility: state.checkDivisionEligibility,
  }));

  // Pending team-division drag move awaiting an eligibility-override decision.
  const [pendingTeamMove, setPendingTeamMove] = useState<any>(null);

  const { registrations, getAllRegistrations } = useRegistrationStore((state: any) => ({
    registrations: state.registrations,
    getAllRegistrations: state.getAllRegistrations,
  }));

  const navigate = useNavigate();

  // ── Tour wiring (clerkId may be absent early in the session — every path no-ops safely) ──
  const admin = useAdminStore((state: any) => state.admin);
  const clerkId = admin?.provider?.id;
  const { completedTours, toursDismissed, markCompleted } = useTourStore();

  const handleStartTour = () => {
    startOrganizeTour(() => {
      if (clerkId) markCompleted(ORGANIZE_TOUR_ID, clerkId, getToken);
    }, navigate);
  };

  // ── Expand/collapse + lazy-load tracking ──
  const [expandedTournaments, setExpandedTournaments] = useState<Set<string>>(new Set());
  const [expandedLeagues, setExpandedLeagues] = useState<Set<string>>(new Set());
  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(new Set());
  const [loadedTournamentContests, setLoadedTournamentContests] = useState<Set<string>>(new Set());
  const [loadedLeagueDivisions, setLoadedLeagueDivisions] = useState<Set<string>>(new Set());
  const [loadedLeagueTeams, setLoadedLeagueTeams] = useState<Set<string>>(new Set());

  // ── Inline name editing ──
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingType, setEditingType] = useState<'tournament' | 'league' | 'division' | null>(null);

  // ── Modal state ──
  const [editConfigId, setEditConfigId] = useState<string | null>(null);
  const [editConfigType, setEditConfigType] = useState<'tournament' | 'league' | 'division' | null>(null);
  const [editPaymentId, setEditPaymentId] = useState<string | null>(null);
  const [editPaymentType, setEditPaymentType] = useState<'tournament' | 'league' | 'division' | null>(null);
  const [editAdminId, setEditAdminId] = useState<string | null>(null);
  const [editAdminType, setEditAdminType] = useState<'tournament' | 'league' | 'division' | null>(null);
  const [editTiebreakerId, setEditTiebreakerId] = useState<string | null>(null);
  const [editTiebreakerType, setEditTiebreakerType] = useState<'tournament' | 'league' | 'division' | null>(null);
  const [editCloneId, setEditCloneId] = useState<string | null>(null);
  const [editCloneType, setEditCloneType] = useState<'tournament' | 'league' | 'division' | null>(null);

  // ── Delete confirmation ──
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string; type: 'tournament' | 'league' | 'division' } | null>(null);
  // Counts of what still references the division queued for deletion. Deleting a
  // division is a SOFT delete with no cascade, so these rows keep pointing at the
  // tombstone and render as "<name> (deleted)" on the registration admin page.
  // null = not loaded / not applicable (non-division target, or the fetch failed).
  // Carries the division id so a slow response for a previously-opened dialog
  // can't paint stale counts onto the one now on screen.
  const [deleteUsage, setDeleteUsage] = useState<{ id: string; divisionTeams: number; registrationTeams: number } | null>(null);

  // ── Active toggle confirmation ──
  const [activeToggleTarget, setActiveToggleTarget] = useState<{
    id: string;
    name: string;
    type: 'tournament' | 'league';
    checked: boolean;
  } | null>(null);

  // ── Inline add (contests/divisions under EXISTING parents only — top-level
  // tournament/league creation goes through CreateCompetitionDialog, ADR 0037) ──
  const [addingChild, setAddingChild] = useState<{ parentId: string | null; type: 'tournament' | 'league' | 'division' } | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [createCompetitionType, setCreateCompetitionType] = useState<'tournament' | 'league' | null>(null);

  // ?create=tournament|league deep-links straight into CreateCompetitionDialog
  // (the ⌘K palette's "Create tournament/league" actions). Effect-based (not a
  // mount-time read) so the action also works while ALREADY on this page; the
  // param is cleared inside the guard with replace:true, so no history entry
  // and no re-trigger loop.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const c = searchParams.get('create');
    if (c === 'tournament' || c === 'league') {
      setCreateCompetitionType(c);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ── DnD sensors ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ── Initial data load ──
  // Auto-expand waits for BOTH hierarchy fetches so the ≤5-rows heuristic sees
  // the full top-level count, not whichever request landed first.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  useEffect(() => {
    Promise.all([
      getTournamentsByLeagueName({ leagueName }),
      getLeaguesByOrganizationName(leagueName),
    ]).finally(() => setInitialLoadDone(true));
    getPaymentProductsByLeagueName(leagueName);
    getAllRegistrations(leagueName);
  }, []);

  // ── Derived data ──
  const sortedTournaments = _.orderBy(tournaments, ['priority', 'name'], ['asc', 'asc']);

  // Sort by manual priority first (drag-to-reorder), then NEWEST-first by startDate
  // as the tiebreaker (matches the public site). `priority || 0` normalizes unset
  // values — leagues carry a mix of null/undefined priority, which lodash orderBy
  // would otherwise split into separate groups and break the startDate tiebreaker.
  // Unset/equal priority → pure newest-first; a dragged league still wins via priority.
  const standaloneLeagues = _.orderBy(
    _.filter(leagues, (l) => l.contestType === 'season' && !l.tournament),
    [(l) => l.priority || 0, (l) => new Date(l.startDate || 0).getTime()],
    ['desc', 'desc']
  );

  const getContestsForTournament = (tid: string) =>
    _.orderBy(
      _.filter(leagues, (l) => helpers.getObjId(l.tournament) === tid),
      [(l) => l.priority || 0, (l) => new Date(l.startDate || 0).getTime()],
      ['desc', 'desc']
    );

  // ── Helpers to look up the right item ──
  const getItem = (id: string, type: 'tournament' | 'league' | 'division') => {
    if (type === 'tournament') return _.find(tournaments, { _id: id });
    if (type === 'league') return _.find(leagues, { _id: id });
    return _.find(divisions, { _id: id });
  };

  // ── Expand handlers ──
  const handleToggleTournament = async (id: string) => {
    const isExpanded = expandedTournaments.has(id);
    if (!isExpanded && !loadedTournamentContests.has(id)) {
      await getContestsByTournamentId(id);
      setLoadedTournamentContests((prev) => new Set([...prev, id]));
    }
    setExpandedTournaments((prev) => {
      const n = new Set(prev);
      isExpanded ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleToggleLeague = async (id: string) => {
    const isExpanded = expandedLeagues.has(id);
    if (!isExpanded && !loadedLeagueDivisions.has(id)) {
      await getDivisionsByLeagueId(id);
      setLoadedLeagueDivisions((prev) => new Set([...prev, id]));
    }
    if (!isExpanded && !loadedLeagueTeams.has(id)) {
      await getTeamDivisionsBySeasonId(id);
      setLoadedLeagueTeams((prev) => new Set([...prev, id]));
    }
    setExpandedLeagues((prev) => {
      const n = new Set(prev);
      isExpanded ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleToggleDivision = (id: string) => {
    setExpandedDivisions((prev) => {
      const n = new Set(prev);
      prev.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // ── Auto-expand on first load ──
  // The tree used to render fully collapsed, hiding that divisions exist at all.
  // ≤5 top-level rows → expand everything; more → expand only the first row of
  // each section as a sample. Reuses the toggle handlers so lazy loads (≤2
  // requests per league) and loaded-set tracking stay in one place.
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (!initialLoadDone || autoExpandedRef.current) return;
    autoExpandedRef.current = true;
    const topLevelCount = sortedTournaments.length + standaloneLeagues.length;
    if (topLevelCount === 0) return;
    const expandAll = topLevelCount <= 5;
    (expandAll ? sortedTournaments : sortedTournaments.slice(0, 1)).forEach((t: any) =>
      handleToggleTournament(t._id)
    );
    (expandAll ? standaloneLeagues : standaloneLeagues.slice(0, 1)).forEach((l: any) =>
      handleToggleLeague(l._id)
    );
  }, [initialLoadDone]);

  // ── One-time tour auto-start ──
  // Fires once per browser for admins who haven't completed/dismissed the tour,
  // only when there's at least one competition for the steps to land on. The
  // localStorage guard is set BEFORE the delay so a quick navigate-away can't
  // re-trigger it, and covers the pre-clerkId window where markCompleted no-ops.
  useEffect(() => {
    if (!initialLoadDone) return;
    if (toursDismissed || completedTours.includes(ORGANIZE_TOUR_ID)) return;
    if (sortedTournaments.length + standaloneLeagues.length === 0) return;
    const guardKey = 'staty-tour-autostart-explore-competitions';
    if (localStorage.getItem(guardKey)) return;
    // Guard is set INSIDE the callback, when the tour actually starts — setting
    // it eagerly here breaks under StrictMode's double-effect (first run stamps
    // the guard, its cleanup clears the timer, second run bails on the guard).
    const timer = setTimeout(() => {
      if (localStorage.getItem(guardKey)) return;
      localStorage.setItem(guardKey, '1');
      handleStartTour();
    }, 800);
    return () => clearTimeout(timer);
  }, [initialLoadDone]);

  // ── Inline editing ──
  const handleStartEdit = (id: string, name: string, type: 'tournament' | 'league' | 'division') => {
    setEditingId(id);
    setEditingName(name);
    setEditingType(type);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingName.trim()) return;
    const item = getItem(editingId, editingType);
    if (!item) return;
    if (editingType === 'tournament') {
      await updateTournament({ ...item, name: editingName.trim() });
    } else if (editingType === 'league') {
      await updateLeague({ ...item, name: editingName.trim() });
    } else {
      const token = await getToken();
      await updateDivision({ ...item, name: editingName.trim() }, token);
    }
    setEditingId(null);
  };

  const handleCancelEdit = () => setEditingId(null);

  // ── Active toggle (called after confirmation) ──
  const handleActiveToggle = async (id: string, type: 'tournament' | 'league', checked: boolean) => {
    const item = getItem(id, type);
    if (!item) return;
    if (type === 'tournament') {
      await updateTournament({ ...item, active: checked });
    } else {
      await updateLeague({ ...item, active: checked });
    }
  };

  // ── Active toggle request (triggers confirmation dialog) ──
  const handleRequestActiveToggle = (id: string, name: string, type: 'tournament' | 'league', checked: boolean) => {
    setActiveToggleTarget({ id, name, type, checked });
  };

  const handleConfirmActiveToggle = async () => {
    if (!activeToggleTarget) return;
    const { id, type, checked } = activeToggleTarget;
    await handleActiveToggle(id, type, checked);
    setActiveToggleTarget(null);
  };

  // ── Options menu handler ──
  // Apply a team's division move and refresh that league's teams (so the row
  // re-renders under the target division with full populations).
  const performTeamMove = async (divisionTeam: any, targetDivisionId: string) => {
    const registrationTeamId = helpers.getObjId(divisionTeam.registrationTeam);
    await updateTeamDivision({ _id: divisionTeam._id, division: targetDivisionId }, registrationTeamId);
    const leagueId = helpers.getObjId(divisionTeam.league);
    if (leagueId) await getTeamDivisionsBySeasonId(leagueId);
  };

  const handleOptionsClick = async (
    action: string,
    id: string,
    type: 'tournament' | 'league' | 'division',
    name?: string,
    data?: any
  ) => {
    if (action === 'move-team') {
      const { divisionTeam, targetDivisionId } = data;
      const targetDivision = _.find(divisions, { _id: targetDivisionId }) || {};
      const registrationTeamId = helpers.getObjId(divisionTeam.registrationTeam);
      // No registration team (manually-added team) → nothing to gate; just move.
      if (!registrationTeamId) { await performTeamMove(divisionTeam, targetDivisionId); return; }
      const token = await getToken();
      const { ok, reasons, isFull } = await checkDivisionEligibility(registrationTeamId, targetDivisionId, token);
      // Full target division → hard block, move is voided (no override).
      if (isFull) {
        toast.error(`${targetDivision.name || 'That division'} is full. Team not moved.`);
        return;
      }
      if (ok) { await performTeamMove(divisionTeam, targetDivisionId); return; }
      setPendingTeamMove({ divisionTeam, targetDivisionId, toDivisionName: targetDivision.name, reasons });
      return;
    }
    if (action === 'settings') { setEditConfigId(id); setEditConfigType(type); }
    else if (action === 'payment') { setEditPaymentId(id); setEditPaymentType(type); }
    else if (action === 'admin') { setEditAdminId(id); setEditAdminType(type); }
    else if (action === 'tiebreaker') { setEditTiebreakerId(id); setEditTiebreakerType(type); }
    else if (action === 'clone') { setEditCloneId(id); setEditCloneType(type); }
    else if (action === 'delete') {
      setDeleteTarget({ id, name: name!, type });
      setDeleteUsage(null);
      if (type === 'division') {
        // Fire-and-forget: the dialog opens immediately and grows the warning
        // line once the counts land, rather than blocking on the request.
        const token = await getToken();
        const usage = await getDivisionUsage(id, token);
        if (usage) setDeleteUsage({ id, ...usage });
      }
    }
    else if (action === 'reorder-division') {
      const token = await getToken();
      await updateDivision(data, token);
    } else if (action === 'reorder-league') {
      await updateLeague(data);
    } else if (action === 'reorder-tournament') {
      await updateTournament(data);
    }
  };

  // ── Top-level tournament drag ──
  const handleTournamentDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = sortedTournaments.findIndex((t) => t._id === active.id);
    const newIdx = sortedTournaments.findIndex((t) => t._id === over.id);
    const reordered = arrayMove([...sortedTournaments], oldIdx, newIdx);
    reordered.forEach((t, i) => handleOptionsClick('reorder-tournament', t._id, 'tournament', null, { ...t, priority: i + 1 }));
  };

  // ── Top-level standalone league drag ──
  const handleStandaloneLeagueDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = standaloneLeagues.findIndex((l) => l._id === active.id);
    const newIdx = standaloneLeagues.findIndex((l) => l._id === over.id);
    const reordered = arrayMove([...standaloneLeagues], oldIdx, newIdx);
    reordered.forEach((l, i) => updateLeague({ ...l, priority: reordered.length - i }));
  };

  // ── Add child ──
  const handleAddChild = (parentId: string | null, type: 'tournament' | 'league' | 'division') => {
    setAddingChild({ parentId, type });
    setNewItemName('');
  };

  const handleAddItem = async () => {
    if (!newItemName.trim() || !addingChild) return;
    const { type, parentId } = addingChild;
    // Top-level tournament/league creation moved to CreateCompetitionDialog
    // (ADR 0037) — this handler only adds children under existing parents.
    if (type === 'league' && parentId) {
      await addLeague({
        name: newItemName.trim(),
        organization: organization?._id,
        tournament: parentId,
        contestType: 'tournament',
        priority: getContestsForTournament(parentId).length + 1,
      });
    } else if (type === 'division' && parentId) {
      await addDivision({
        league: parentId,
        organization: organization?._id,
        name: newItemName.trim(),
        priority: _.filter(divisions, (d) => helpers.getObjId(d.league) === parentId).length + 1,
      });
    }
    setNewItemName('');
    setAddingChild(null);
  };

  const handleCancelAdd = () => { setAddingChild(null); setNewItemName(''); };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { id, type } = deleteTarget;
    if (type === 'tournament') await deleteTournament(id);
    else if (type === 'league') await deleteLeague(id);
    else {
      const token = await getToken();
      await deleteDivision(id, token);
    }
    setDeleteTarget(null);
    setDeleteUsage(null);
  };

  // ── Modal submit handlers ──
  const handleSettingsUpdate = async (values: any, topLevel?: { startDate?: string }) => {
    const item = getItem(editConfigId, editConfigType);
    if (!item) return;
    // Constraints arrive in `values.constraints` and land under
    // `config.constraints` via the same spread as every other config field —
    // they inherit through the standard division > league > tournament
    // hierarchy alongside capacity, tiebreakers, etc.
    // `topLevel.startDate` is a TOP-LEVEL entity field (tournament/league) —
    // it deliberately bypasses the config subdoc and its inherit semantics.
    const updated = {
      ...item,
      ...(topLevel?.startDate ? { startDate: topLevel.startDate } : {}),
      config: { ...item.config, ...values },
    };
    if (editConfigType === 'tournament') await updateTournament(updated);
    else if (editConfigType === 'league') await updateLeague(updated);
    else { const token = await getToken(); await updateDivision(updated, token); }
  };

  const handlePaymentUpdate = async (values: any) => {
    const item = getItem(editPaymentId, editPaymentType);
    if (!item) return;
    if (values.selectedPayment) {
      // The fee lives at config.paymentProduct (ADR 0034) — spread the existing
      // config since the server $sets the whole subdoc.
      const updated = {
        ...item,
        config: { ...item.config, paymentProduct: values.selectedPayment._id },
      };
      if (editPaymentType === 'tournament') await updateTournament(updated);
      else if (editPaymentType === 'league') await updateLeague(updated);
      else { const token = await getToken(); await updateDivision(updated, token); }
    }
    // (No create branch: since 2026-08-25 the Payment modal's create tab is a
    // link to the registration Payments pane — products are made there.)
  };

  const handleAdminUpdate = async (values: any) => {
    const item = getItem(editAdminId, editAdminType);
    if (!item) return;
    const updated = { ...item, ...values };
    if (editAdminType === 'tournament') await updateTournament(updated);
    else if (editAdminType === 'league') await updateLeague(updated);
    else { const token = await getToken(); await updateDivision(updated, token); }
  };

  const handleTiebreakerUpdate = async (sportId: string, tiebreakerConfigs: any[], general: any = {}) => {
    const item = getItem(editTiebreakerId, editTiebreakerType);
    if (!item) return;
    const parentCfg = resolveParentConfig(item, editTiebreakerType);

    // Tiebreaker chains follow the same write-only-differences rule as the
    // seed toggle below (ADR 0032): the modal seeds from the EFFECTIVE chain,
    // so persisting it unconditionally would stamp the inherited chain onto
    // this entity and a later parent edit could never reach it again. Rebuild
    // `tiebreakers` keeping only MEANINGFUL chains for other sports — legacy
    // stamped-empty arrays (the pre-2026-09-23 Mongoose default bug) drop on
    // the way, since the server $sets the whole config subdoc.
    const tiebreakers: Record<string, any[]> = {};
    Object.entries(item.config?.tiebreakers || {}).forEach(([s, chain]) => {
      if (s !== sportId && Array.isArray(chain) && chain.length > 0) tiebreakers[s] = chain;
    });
    const persistChain = chainOrInherit(parentCfg, sportId, tiebreakerConfigs);
    if (persistChain !== undefined) tiebreakers[sportId] = persistChain;
    const config: any = { ...item.config };
    if (Object.keys(tiebreakers).length > 0) config.tiebreakers = tiebreakers;
    else delete config.tiebreakers;

    // Sport-agnostic — lives on config directly, saved by whichever sport tab
    // the admin clicked Save on. Written ONLY when it differs from what this
    // entity would inherit (ADR 0032): the toggle is seeded from the effective
    // value, so persisting it unconditionally stamped the inherited value onto
    // every entity whose Tiebreakers were ever saved — a division that once
    // saved stat order with the toggle off carried an explicit `false` that a
    // tournament-level `true` could never reach again. Omitting the key (the
    // server $sets the whole config subdoc) both inherits AND cleans an old
    // stamp when the toggle is set back to the inherited value.
    const seedValue = boolOrInherit(
      parentCfg,
      'breakPlayoffTiesBySeed',
      !!general.breakPlayoffTiesBySeed,
      CONFIG_DEFAULTS.breakPlayoffTiesBySeed,
    );
    if (seedValue === undefined) delete config.breakPlayoffTiesBySeed;
    else config.breakPlayoffTiesBySeed = seedValue;

    const updated = { ...item, config };
    if (editTiebreakerType === 'tournament') await patchTournament(updated);
    else if (editTiebreakerType === 'league') await updateLeague(updated);
    else { const token = await getToken(); await updateDivision(updated, token); }
  };

  const handleClone = async (values: { seasonName: string; cloneDivisions: boolean; cloneRegistration?: boolean }) => {
    if (!editCloneId) return;
    // Explicit branches, not an if/else — the `else` used to catch everything,
    // so adding a third type silently cloned a LEAGUE instead.
    if (editCloneType === 'tournament') {
      await cloneTournament(editCloneId, values.seasonName, values.cloneDivisions, !!values.cloneRegistration);
    } else if (editCloneType === 'league') {
      await cloneLeague(editCloneId, values.seasonName, values.cloneDivisions);
    } else if (editCloneType === 'division') {
      // A division has no children, so the modal's clone-children/registration
      // booleans don't apply.
      await cloneDivision(editCloneId, values.seasonName);
    }
    setEditCloneId(null);
    setEditCloneType(null);
  };

  // ── Current modal item ──
  const currentConfigItem = editConfigId ? getItem(editConfigId, editConfigType) : null;

  // Resolve the parent's effective config so the Settings modal can pre-populate
  // inherited values (capacity, waitlist, eligibility, etc.) when the item has
  // no own override. Division parent = league (+ league's tournament).
  // League parent = tournament.
  // Resolve what `item` would INHERIT (its parents only, not its own config).
  // Used by the Settings drawer and the Tiebreakers modal so both can seed
  // toggles from the effective value and write only differences (ADR 0032).
  const resolveParentConfig = (item: any, type: 'tournament' | 'league' | 'division' | null) => {
    if (!item || !type) return {};
    if (type === 'tournament') return {};

    let l = null;
    let t = null;
    if (type === 'league') {
      const tId = item.tournament ? helpers.getObjId(item.tournament) : null;
      t = tId ? _.find(tournaments, { _id: tId }) : null;
    } else {
      // division
      const lId = item.league ? helpers.getObjId(item.league) : null;
      l = lId ? _.find(leagues, { _id: lId }) : null;
      const tId = l?.tournament ? helpers.getObjId(l.tournament) : null;
      t = tId ? _.find(tournaments, { _id: tId }) : null;
    }
    // Resolve with the SHARED resolver the server uses, not a hand-rolled merge,
    // so the drawer and the API can never disagree about what is inherited.
    // getConfig also handles eligibility per-sub-key (an empty league gender
    // falls through to the tournament's instead of shadowing it), which this
    // block used to re-implement by hand.
    return getConfig({
      division: {},
      season: l?.config || {},
      tournament: t?.config || {},
    });
  };
  const settingsParentConfig = resolveParentConfig(currentConfigItem, editConfigType);
  // Named parent chain, most-specific first — powers the Settings drawer's
  // inheritance provenance ("Inherited · On from Youth Boys 3-on-3"). Kept
  // separate from resolveParentConfig because the Tiebreakers modal only
  // needs the merged config.
  const resolveParentTiers = (item: any, type: 'tournament' | 'league' | 'division' | null) => {
    if (!item || !type || type === 'tournament') return [];
    const tiers: Array<{ tier: string; name: string; config: any }> = [];
    if (type === 'league') {
      const tId = item.tournament ? helpers.getObjId(item.tournament) : null;
      const t = tId ? _.find(tournaments, { _id: tId }) : null;
      if (t) tiers.push({ tier: 'tournament', name: t.name, config: t.config || {} });
    } else {
      const lId = item.league ? helpers.getObjId(item.league) : null;
      const l = lId ? _.find(leagues, { _id: lId }) : null;
      if (l) tiers.push({ tier: l.tournament ? 'contest' : 'league', name: l.name, config: l.config || {} });
      const tId = l?.tournament ? helpers.getObjId(l.tournament) : null;
      const t = tId ? _.find(tournaments, { _id: tId }) : null;
      if (t) tiers.push({ tier: 'tournament', name: t.name, config: t.config || {} });
    }
    return tiers;
  };
  const settingsParentTiers = resolveParentTiers(currentConfigItem, editConfigType);
  const currentPaymentItem = editPaymentId ? getItem(editPaymentId, editPaymentType) : null;
  const currentAdminItem = editAdminId ? getItem(editAdminId, editAdminType) : null;
  const currentTiebreakerItem = editTiebreakerId ? getItem(editTiebreakerId, editTiebreakerType) : null;
  const currentCloneItem = editCloneId
    ? (editCloneType === 'tournament'
        ? _.find(tournaments, { _id: editCloneId })
        : editCloneType === 'division'
          ? _.find(divisions, { _id: editCloneId })
          : _.find(leagues, { _id: editCloneId }))
    : null;

  // Per-type copy for the shared clone modal. A lookup rather than three nested
  // ternaries per prop. `siblings` feeds the modal's duplicate-name check, so it
  // must be the set the new name actually has to be unique within — for a
  // division that's the other divisions in the SAME contest, not the whole org.
  const CLONE_COPY = {
    tournament: {
      title: 'Clone Tournament',
      nameLabel: 'Tournament Name',
      namePlaceholder: 'Enter tournament name',
      childrenLabel: 'Clone Sections & Divisions',
      description: 'Clone this tournament with a new name. You can optionally clone its sections and divisions as well.',
      duplicateMessage: 'A tournament with this name already exists. Please choose a different name.',
      showChildren: true,
    },
    league: {
      title: 'Clone League',
      nameLabel: 'League Name',
      namePlaceholder: 'Enter league name',
      childrenLabel: 'Clone Divisions',
      description: 'Clone this league with a new name. You can optionally clone divisions as well.',
      duplicateMessage: 'A league with this name already exists. Please choose a different name.',
      showChildren: true,
    },
    division: {
      title: 'Clone Division',
      nameLabel: 'Division Name',
      namePlaceholder: 'Enter division name',
      childrenLabel: '',
      description: "Clone this division with a new name, into the same contest. Its settings (tiebreakers, capacity, format and eligibility) are copied. Teams, games and pricing are not: the copy inherits its price from the parent.",
      duplicateMessage: 'A division with this name already exists. Please choose a different name.',
      // A division has no children to clone, so the checkbox is hidden.
      showChildren: false,
    },
  };
  const cloneCopy = CLONE_COPY[editCloneType || 'league'];
  const cloneSiblings = editCloneType === 'tournament'
    ? tournaments
    : editCloneType === 'division'
      ? _.filter(divisions, (d: any) => helpers.getObjId(d.league) === helpers.getObjId(currentCloneItem?.league))
      : leagues;

  // Drawer eyebrow: the entity a kebab editor is acting on, e.g.
  // "Division · Men's Open". The action word (Settings/Payment/…) stays the
  // prominent title; this is the smaller, muted context line above it.
  const editorSubtitle = (type: string | null, item: any) =>
    [_.capitalize(type || ''), item?.name].filter(Boolean).join(' · ');

  // ── Render ──
  return (
    <PageContainer>
      <PageHeader title="Competitions" />

      {/* Hint bar — the tree's core interactions are hover/double-click and
          otherwise invisible; this line + the tour are their discoverability. */}
      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1 -mt-4 text-sm text-muted-foreground"
        data-tour="organize-hint-bar"
      >
        <span>
          Click <span className="font-medium">▸</span> to see contests &amp; divisions
          {' · '}double-click a name to rename
          {' · '}hover a row for more actions (⋯)
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-primary"
          data-tour="organize-take-tour"
          onClick={handleStartTour}
        >
          <Sparkles className="h-4 w-4 mr-1" /> Take a tour
        </Button>
      </div>

      {/* Tree skeleton during the FIRST load when nothing is cached — the
          module-singleton stores keep data across navigations, so a cached
          tree paints immediately and refreshes silently. */}
      {!initialLoadDone && sortedTournaments.length === 0 && standaloneLeagues.length === 0 ? (
        <ListSkeleton />
      ) : (
      <>
      {/* TOURNAMENTS SECTION */}
      <section className="space-y-1" data-tour="organize-tournaments">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Trophy className="h-5 w-5" /> Tournaments
          </h2>
          <Button size="sm" data-tour="organize-add-tournament" onClick={() => setCreateCompetitionType('tournament')}>
            <Plus className="h-4 w-4 mr-1" /> Add Tournament
          </Button>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTournamentDragEnd}>
          <SortableContext items={sortedTournaments.map((t) => t._id)} strategy={verticalListSortingStrategy}>
            {sortedTournaments.map((tournament) => (
              <SortableRow key={tournament._id} id={tournament._id}>
                {({ dragListeners }) => (
                  <TournamentRow
                    tournament={tournament}
                    registration={_.find(registrations, (r) => helpers.getObjId(r.tournament) === tournament._id)}
                    onNavigate={navigate}
                    isExpanded={expandedTournaments.has(tournament._id)}
                    onToggle={() => handleToggleTournament(tournament._id)}
                    contests={getContestsForTournament(tournament._id)}
                    orgHasSport={!!organization?.sports?.length}
                    expandedLeagues={expandedLeagues}
                    onToggleLeague={handleToggleLeague}
                    divisions={divisions}
                    teamDivisions={teamDivisions}
                    expandedDivisions={expandedDivisions}
                    onToggleDivision={handleToggleDivision}
                    loadedLeagueTeams={loadedLeagueTeams}
                    paymentProducts={paymentProducts}
                    editingId={editingId}
                    editingName={editingName}
                    editingType={editingType}
                    onStartEdit={handleStartEdit}
                    onEditNameChange={setEditingName}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onRequestActiveToggle={handleRequestActiveToggle}
                    onOptionsClick={handleOptionsClick}
                    onAddChild={handleAddChild}
                    addingChild={addingChild}
                    newItemName={newItemName}
                    onNewItemNameChange={setNewItemName}
                    onAddItem={handleAddItem}
                    onCancelAdd={handleCancelAdd}
                    dragListeners={dragListeners}
                  />
                )}
              </SortableRow>
            ))}
          </SortableContext>
        </DndContext>
        {initialLoadDone && sortedTournaments.length === 0 && (
          <EmptyState
            className="p-8"
            icon={Trophy}
            title="No tournaments yet"
            description="A tournament holds contests and divisions for a single event: brackets, pools, and one-day play."
            action={(
              <Button size="sm" variant="outline" onClick={() => setCreateCompetitionType('tournament')}>
                <Plus className="h-4 w-4 mr-1" /> Add Tournament
              </Button>
            )}
          />
        )}
      </section>

      {/* STANDALONE LEAGUES SECTION */}
      <section className="space-y-1" data-tour="organize-leagues">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CalendarDays className="h-5 w-5" /> Leagues
          </h2>
          <Button size="sm" data-tour="organize-add-league" onClick={() => setCreateCompetitionType('league')}>
            <Plus className="h-4 w-4 mr-1" /> Add League
          </Button>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStandaloneLeagueDragEnd}>
          <SortableContext items={standaloneLeagues.map((l) => l._id)} strategy={verticalListSortingStrategy}>
            {standaloneLeagues.map((league) => (
              <SortableRow key={league._id} id={league._id}>
                {({ dragListeners }) => (
                  <LeagueRow
                    league={league}
                    registration={_.find(registrations, (r) => helpers.getObjId(r.contest) === league._id)}
                    orgHasSport={!!organization?.sports?.length}
                    onNavigate={navigate}
                    isExpanded={expandedLeagues.has(league._id)}
                    onToggle={() => handleToggleLeague(league._id)}
                    divisions={_.orderBy(
                      _.filter(divisions, (d) => helpers.getObjId(d.league) === league._id),
                      ['priority', 'name'],
                      ['desc', 'asc']
                    )}
                    paymentProducts={paymentProducts}
                    editingId={editingId}
                    editingName={editingName}
                    editingType={editingType}
                    onStartEdit={handleStartEdit}
                    onEditNameChange={setEditingName}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onRequestActiveToggle={handleRequestActiveToggle}
                    onOptionsClick={handleOptionsClick}
                    onAddChild={handleAddChild}
                    addingChild={addingChild}
                    newItemName={newItemName}
                    onNewItemNameChange={setNewItemName}
                    onAddItem={handleAddItem}
                    onCancelAdd={handleCancelAdd}
                    teamDivisions={teamDivisions}
                    expandedDivisions={expandedDivisions}
                    onToggleDivision={handleToggleDivision}
                    loadedLeagueTeams={loadedLeagueTeams}
                    dragListeners={dragListeners}
                  />
                )}
              </SortableRow>
            ))}
          </SortableContext>
        </DndContext>
        {initialLoadDone && standaloneLeagues.length === 0 && (
          <EmptyState
            className="p-8"
            icon={CalendarDays}
            title="No leagues yet"
            description="A league is an ongoing season with divisions, schedules, and standings."
            action={(
              <Button size="sm" variant="outline" onClick={() => setCreateCompetitionType('league')}>
                <Plus className="h-4 w-4 mr-1" /> Add League
              </Button>
            )}
          />
        )}
      </section>
      </>
      )}

      {/* MODALS */}
      {createCompetitionType && (
        <CreateCompetitionDialog
          open={!!createCompetitionType}
          onOpenChange={(o) => { if (!o) setCreateCompetitionType(null); }}
          type={createCompetitionType}
          organization={organization}
          onCreated={async () => {
            // Refresh everything the new rows render from — including
            // registrations, so the per-row registration icon reflects the
            // just-created registration without a reload.
            await Promise.all([
              getTournamentsByLeagueName({ leagueName }),
              getLeaguesByOrganizationName(leagueName),
              getAllRegistrations(leagueName),
            ]);
          }}
        />
      )}
      {editConfigId && currentConfigItem && (
        <SettingsModal
          open={!!editConfigId}
          onClose={() => { setEditConfigId(null); setEditConfigType(null); }}
          onSubmit={handleSettingsUpdate}
          subtitle={editorSubtitle(editConfigType, currentConfigItem)}
          data={currentConfigItem}
          defaultConfig={settingsParentConfig}
          parentTiers={settingsParentTiers}
          entityType={editConfigType}
        />
      )}
      {editPaymentId && currentPaymentItem && (
        <PaymentModal
          open={!!editPaymentId}
          onClose={() => { setEditPaymentId(null); setEditPaymentType(null); }}
          onSubmit={handlePaymentUpdate}
          subtitle={editorSubtitle(editPaymentType, currentPaymentItem)}
          currentProduct={currentPaymentItem}
          products={paymentProducts}
        />
      )}
      {editAdminId && currentAdminItem && (
        <AdminModal
          open={!!editAdminId}
          onClose={() => { setEditAdminId(null); setEditAdminType(null); }}
          onSubmit={handleAdminUpdate}
          subtitle={editorSubtitle(editAdminType, currentAdminItem)}
          data={currentAdminItem}
        />
      )}
      {editTiebreakerId && currentTiebreakerItem && (() => {
        // Seed the playoff-seed toggle from the EFFECTIVE value (own if set,
        // else inherited) and tell the modal where an inherited value comes
        // from — the raw own value made a tournament-level `true` look like it
        // "hadn't cascaded" every time the modal was opened on a child.
        const tiebreakerParentConfig = resolveParentConfig(currentTiebreakerItem, editTiebreakerType);
        const ownSeed = currentTiebreakerItem.config?.breakPlayoffTiesBySeed;
        const effectiveSeed = typeof ownSeed === 'boolean'
          ? ownSeed
          : inheritedBool(tiebreakerParentConfig, 'breakPlayoffTiesBySeed', CONFIG_DEFAULTS.breakPlayoffTiesBySeed);
        const seedInheritedFrom = typeof ownSeed === 'boolean'
          ? null
          : typeof tiebreakerParentConfig.breakPlayoffTiesBySeed === 'boolean'
            ? (editTiebreakerType === 'division' ? 'the contest / tournament' : 'the tournament')
            : null;
        // Same for the CHAIN itself: seed from the effective chain (own
        // non-empty chain, else the parent's — resolveParentConfig runs the
        // shared getConfig, whose meaningful-guard already drops stamped-empty
        // arrays), with an attribution note when the shown chain is inherited.
        // handleTiebreakerUpdate writes only differences, so saving an
        // untouched inherited chain does NOT stamp it.
        const sport = organization?.sports?.[0];
        const ownChain = sport ? currentTiebreakerItem.config?.tiebreakers?.[sport] : undefined;
        const hasOwnChain = Array.isArray(ownChain) && ownChain.length > 0;
        const parentChain = sport ? tiebreakerParentConfig.tiebreakers?.[sport] : undefined;
        const effectiveTiebreakers = hasOwnChain
          ? { [sport]: ownChain }
          : parentChain ? { [sport]: parentChain } : {};
        const tiebreakersInheritedFrom = !hasOwnChain && parentChain
          ? (editTiebreakerType === 'division' ? 'the contest / tournament' : 'the tournament')
          : null;
        return (
          <TiebreakerModal
            open={!!editTiebreakerId}
            onClose={() => { setEditTiebreakerId(null); setEditTiebreakerType(null); }}
            onSubmit={handleTiebreakerUpdate}
            title="Tiebreakers"
            subtitle={editorSubtitle(editTiebreakerType, currentTiebreakerItem)}
            tiebreakers={effectiveTiebreakers}
            generalSettings={{ breakPlayoffTiesBySeed: effectiveSeed }}
            seedInheritedFrom={seedInheritedFrom}
            tiebreakersInheritedFrom={tiebreakersInheritedFrom}
            sports={sport ? [sport] : []}
          />
        );
      })()}
      {editCloneId && currentCloneItem && (
        <CloneLeagueModal
          open={!!editCloneId}
          onClose={() => { setEditCloneId(null); setEditCloneType(null); }}
          onSubmit={handleClone}
          data={currentCloneItem}
          seasons={cloneSiblings}
          copy={cloneCopy}
          // Resolve via the registrations store (Registration.tournament →
          // Tournament), NOT `tournament.registration` — the Tournament schema
          // has no such path; only stale raw legacy-/admin fields ever made
          // that truthy, so normally-created tournaments never saw the option.
          showRegistrationCheckbox={editCloneType === 'tournament'
            && _.some(registrations, (r: any) => helpers.getObjId(r.tournament) === editCloneId)}
        />
      )}

      {/* ACTIVE TOGGLE CONFIRMATION */}
      <AlertDialog open={!!activeToggleTarget} onOpenChange={(open) => !open && setActiveToggleTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {activeToggleTarget?.checked ? 'Activate' : 'Deactivate'} {activeToggleTarget?.type}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {activeToggleTarget?.checked ? 'activate' : 'deactivate'} &quot;{activeToggleTarget?.name}&quot;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmActiveToggle}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DELETE CONFIRMATION */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteUsage(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
            </AlertDialogDescription>
            {/* Deleting a division does NOT cascade — anything counted here keeps
                referencing it and will read "<name> (deleted)" on the registration
                page. Warn, but don't block: removing a division with teams is a
                legitimate admin action. */}
            {deleteTarget?.type === 'division'
              && deleteUsage?.id === deleteTarget.id
              && (deleteUsage.registrationTeams > 0 || deleteUsage.divisionTeams > 0) && (
              <div className="mt-3 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
                {deleteUsage.registrationTeams > 0 && (
                  <div>
                    <strong>{deleteUsage.registrationTeams}</strong>
                    {deleteUsage.registrationTeams === 1 ? ' registration' : ' registrations'} still
                    {' '}reference this division.
                  </div>
                )}
                {deleteUsage.divisionTeams > 0 && (
                  <div>
                    <strong>{deleteUsage.divisionTeams}</strong>
                    {deleteUsage.divisionTeams === 1 ? ' team is' : ' teams are'} placed in it.
                  </div>
                )}
                <div className="mt-1">
                  They won&apos;t be deleted. They&apos;ll keep pointing at this division and show as
                  {' '}&quot;{deleteTarget.name} (deleted)&quot; on the registration page, and they&apos;ll
                  {' '}drop out of division capacity and waitlist counts. Move them to another division
                  {' '}first if you want them to stay counted.
                </div>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MoveTeamEligibilityDialog
        open={!!pendingTeamMove}
        teamName={pendingTeamMove ? getTeamDisplayName(pendingTeamMove.divisionTeam) : ''}
        toDivisionName={pendingTeamMove?.toDivisionName}
        reasons={pendingTeamMove?.reasons || []}
        onConfirm={() => {
          if (pendingTeamMove) performTeamMove(pendingTeamMove.divisionTeam, pendingTeamMove.targetDivisionId);
          setPendingTeamMove(null);
        }}
        onCancel={() => setPendingTeamMove(null)}
      />
    </PageContainer>
  );
}

export default OrganizeAdmin;
